import { Paper } from '@e3dc-react/shell/Elements/Paper'
import { useCountryOptions } from '@e3dc-react/shell/Hooks/useCountryOptions'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Button, Divider, Grid, Typography } from '@mui/material'
import { useFormikContext } from 'formik'
import React from 'react'
import { useUserRoles } from 'src/Hooks/user'
import { TextField } from 'src/Libs/inputs/formik'
import Checkbox from 'src/Libs/inputs/formik/Checkbox'
import Select from 'src/Libs/inputs/formik/Select'
import { USER_PROFILE_DATA_ID } from 'src/Libs/qa/ids'
import { AccountProfileForm } from 'src/Libs/user/types'
import { isFlowTheme } from 'src/Libs/utils'
import { makeStyles } from 'tss-react/mui'
import { InformationBox } from 'src/Components'

const useStyles = makeStyles()(theme => ({
  form: {
    marginTop: theme.spacing(2),
  },
  checkboxRowContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  divider: {
    borderColor: theme.palette.grey.grey06,
    marginLeft: `-${theme.spacing(4)}`,
    marginRight: `-${theme.spacing(4)}`,
  },
  footer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'end',
      flex: 1,
    },
  },
}))

interface Props {
  title: string
}

const UserProfileForm: React.FC<Props> = ({ title }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const { handleSubmit, dirty, isValid } = useFormikContext<AccountProfileForm>()
  const { allCountries } = useCountryOptions()
  const { isUser } = useUserRoles()

  return (
    <Paper title={title} dataQa={USER_PROFILE_DATA_ID}>
      <InformationBox text={t('administration.users.address-message')} messageType={'info'} sx={{ mt: 2 }} />
      <form className={classes.form} onSubmit={handleSubmit}>
        <Grid container justifyContent={'space-between'}>
          <Grid item xs={12} sm={6} marginBottom={2}>
            <TextField label={t('common.login.user')} name={'username'} disabled />
          </Grid>
          {!isFlowTheme && (
            <Grid item xs={12} sm={6} marginBottom={2}>
              <TextField label={t('common.email')} name={'eMail'} disabled={!isUser} />
            </Grid>
          )}

          <Grid item xs={12} sm={6} marginBottom={2}>
            <TextField label={`${t('common.firstName')}`} required name={'firstName'} />
          </Grid>
          <Grid item xs={12} sm={6} marginBottom={2}>
            <TextField label={`${t('common.lastName')}`} required name={'lastName'} />
          </Grid>
          <Grid item xs={12} sm={6} marginBottom={2} data-qa="address">
            <TextField label={t('common.street')} name={'address'} />
          </Grid>
          <Grid item xs={12} sm={3} marginBottom={2}>
            <TextField label={t('common.zipCode')} name={'zipCode'} />
          </Grid>
          <Grid item xs={12} sm={3} marginBottom={2}>
            <TextField label={t('common.location')} name={'city'} />
          </Grid>
          <Grid item xs={12} sm={6} marginBottom={2}>
            <Select label={t('common.country')} name={'country'} options={allCountries} isSearchable />
          </Grid>
          <Grid item xs={12} sm={6} marginBottom={2}>
            <TextField label={t('common.phone')} name={'phoneNumber'} />
          </Grid>
          <Grid item xs={12} marginBottom={2} data-qa="email-notifications">
            <div className={classes.checkboxRowContainer}>
              <Checkbox name={'eMailNotification'} sx={{ padding: 0, mr: 1 }} />
              <Typography variant="body2">{t('administration.users.emailNotification')}</Typography>
            </div>
          </Grid>
        </Grid>

        <Divider className={classes.divider} />

        <div className={classes.footer}>
          <Button type="submit" variant="contained" size="large" color="primary" disabled={!dirty || !isValid} data-qa="save-button">
            {t('common.save')}
          </Button>
        </div>
      </form>
    </Paper>
  )
}

export default UserProfileForm
